import produce from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { useGetCustomFormValuesQuery } from "@/apollo/types";
import { SecondaryButton } from "@/components/elements/Button";
import { AppliedMultiSelect } from "@/components/elements/Select";
import FieldLabel from "@/components/primitives/InputLabel";
import { FormType } from "@/integrations";

import FacebookAdsCustomReport from "./FacebookAdsCustomReport";
import { InputDescription } from "@/components/primitives/InputDescription";

export type BasicFormOption = { label: string; value: string };

export enum FACEBOOK_ADS_CLICK_WINDOW {
  CLICK_1D = "1d_click",
  CLICK_7D = "7d_click",
  CLICK_28D = "28d_click",
}

const facebookAdsClickWindowOptions: BasicFormOption[] = Object.values(
  FACEBOOK_ADS_CLICK_WINDOW,
).map((value) => ({
  label: value,
  value,
}));

export enum FACEBOOK_ADS_VIEW_WINDOW {
  VIEW_1D = "1d_view",
  VIEW_7D = "7d_view",
  VIEW_28D = "28d_view",
}

const facebookAdsViewWindowOptions: BasicFormOption[] = Object.values(
  FACEBOOK_ADS_VIEW_WINDOW,
).map((value) => ({
  label: value,
  value,
}));

export type FacebookAdsReportOption = {
  label: string;
  value: string;
};

export const FACEBOOK_ADS_DAILY_AGGREGATION: FacebookAdsReportOption = {
  label: "daily",
  value: "daily",
};

export type FacebookAdsReport = {
  name: string;
  id: string;
  breakdowns: FacebookAdsReportOption[];
  action_breakdowns: FacebookAdsReportOption[];
  levels: FacebookAdsReportOption;
  action_report_time: FacebookAdsReportOption;
  fields: FacebookAdsReportOption[];
  view_window: FacebookAdsReportOption[];
  click_window: FacebookAdsReportOption[];
  aggregation: FacebookAdsReportOption;
  /**
   * Trailing window length: a bare number of days ("30"), or calendar months with an "m" suffix
   * ("12m") - do not read it as a plain day count. Optional, and mutually exclusive with
   * `aggregation`: aggregation produces calendar-aligned buckets (Monday-aligned weeks, calendar
   * months), whereas this produces a window that slides with each report date - per day for a day
   * window, per calendar month for a month one. Required for a deduplicated reach/frequency figure,
   * since Facebook only deduplicates within the window it is asked for.
   */
  rolling_window?: FacebookAdsReportOption;
};

const normalizeReport = (report: any): FacebookAdsReport => {
  const normalizeOptionValues = (
    option?: FacebookAdsReportOption | FacebookAdsReportOption[] | undefined,
  ): FacebookAdsReportOption[] => {
    if (!option) {
      return [];
    }
    if (Array.isArray(option)) {
      return option;
    }
    return [option];
  };

  return {
    ...report,
    breakdowns: report?.breakdowns ?? [],
    action_breakdowns: report?.action_breakdowns ?? [],
    fields: report?.fields ?? [],
    levels: report?.levels ?? { label: "ad", value: "ad" },
    action_report_time: report?.action_report_time ?? {
      label: "impression",
      value: "impression",
    },
    click_window: normalizeOptionValues(report?.click_window),
    view_window: normalizeOptionValues(report?.view_window),
    aggregation: report?.aggregation ?? FACEBOOK_ADS_DAILY_AGGREGATION,
    // Left undefined when absent: the backend reads a missing rolling_window as "not rolling",
    // so it must not be defaulted to a value the way aggregation is.
    rolling_window: report?.rolling_window ?? undefined,
  };
};

const useCustomFormFieldsQuery = (connectionId: string) => {
  const { data, loading, refetch } = useGetCustomFormValuesQuery({
    variables: {
      input: {
        connectionId: connectionId,
        dependencies: "{}",
      },
    },
    onError(_error) {
      return [];
    },
  });
  return { data: data?.getCustomFormValues.customFormValues, loading, refetch };
};

const getOptions = (customFieldsData: any) => {
  const mapToDisplay = (values: string[]) =>
    (values || []).map((f: string) => ({ label: f, value: f }));
  const breakdownOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.breakdowns,
  );
  const actionBreakdownOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.action_breakdowns,
  );
  const fieldsOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.fields,
  );
  const levelOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.levels,
  );
  const actionReportTimeOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.action_report_time,
  );

  const clickWindowOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.click_window,
  );
  const viewWindowOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.view_window,
  );
  const aggregationOptions: BasicFormOption[] = mapToDisplay(
    customFieldsData?.aggregation,
  );
  const rollingWindowValues: string[] = customFieldsData?.rolling_window ?? [];
  // Day windows first, each group by length: the backend sends them in enum order.
  const rollingWindowOptions: BasicFormOption[] = rollingWindowValues
    .map((value) => {
      const months = /^(\d+)m$/.exec(value);
      return {
        label: months
          ? `Last ${months[1]} months (one row per month)`
          : `Last ${value} days`,
        value,
        sortKey: months ? 10000 + Number(months[1]) : Number(value),
      };
    })
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ label, value }) => ({ label, value }));
  return {
    breakdownOptions,
    actionBreakdownOptions,
    fieldsOptions,
    levelOptions,
    actionReportTimeOptions,
    clickWindowOptions,
    viewWindowOptions,
    aggregationOptions,
    rollingWindowOptions,
  };
};

export default function FacebookAdsForm(props: {
  field: Extract<FormType, { type: "facebook-ads-form" }>;
}) {
  const context = useFormContext();
  const [adaccount, setAdAccount] = useState<BasicFormOption[] | undefined>(
    context.getValues()["adaccount"],
  );
  const [clickWindow, setClickWindow] = useState<BasicFormOption[] | undefined>(
    context.getValues()["click_window"] ?? [
      {
        label: FACEBOOK_ADS_CLICK_WINDOW.CLICK_7D,
        value: FACEBOOK_ADS_CLICK_WINDOW.CLICK_7D,
      },
    ],
  );
  const [viewWindow, setViewWindow] = useState<BasicFormOption[] | undefined>(
    context.getValues()["view_window"] ?? [
      {
        label: FACEBOOK_ADS_VIEW_WINDOW.VIEW_1D,
        value: FACEBOOK_ADS_VIEW_WINDOW.VIEW_1D,
      },
    ],
  );

  const accounts = props.field.accounts;

  const [reportErrors, setReportErrors] = useState<Record<string, string>>({});
  const [invalidCombinationReportIds, setInvalidCombinationReportIds] =
    useState<string[]>([]);

  const { data: customFieldsData } = useCustomFormFieldsQuery(
    context.getValues()["connectionId"],
  );

  const {
    actionBreakdownOptions,
    breakdownOptions,
    fieldsOptions,
    levelOptions,
    actionReportTimeOptions,
    viewWindowOptions,
    aggregationOptions,
    clickWindowOptions,
    rollingWindowOptions,
  } = getOptions(customFieldsData);

  const adAccountOptions: any[] = useMemo(() => {
    return accounts.map((account) => ({
      label: `${account.value} (${account.label})`,
      value: account.value,
      disabled: false,
    }));
  }, [accounts]);

  useEffect(() => {
    context.register("custom-reports", { required: false });
    context.register("adaccount", { required: true });
    context.register("view_window", { required: false });
    context.register("click_window", { required: false });
  }, [context]);

  const getInitialReports = (): FacebookAdsReport[] => {
    const values = context.getValues()["custom-reports"];
    if (values === "NO_REPORTS") return [];
    return (values ?? []).filter((v: any) => !!v).map(normalizeReport);
  };

  const [reports, setReports] = useState<FacebookAdsReport[]>(() =>
    getInitialReports(),
  );

  const changeReport = (report: FacebookAdsReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        const cur = draft.find((rep) => rep.id === report.id);
        if (cur) {
          cur.name = report.name;
          cur.breakdowns = report.breakdowns;
          cur.fields = report.fields;
          cur.action_breakdowns = report.action_breakdowns;
          cur.levels = report.levels;
          cur.action_report_time = report.action_report_time;
          cur.click_window = report.click_window;
          cur.view_window = report.view_window;
          cur.aggregation = report.aggregation;
          cur.rolling_window = report.rolling_window;
        }
      }),
    );
  };

  const removeReport = (report: FacebookAdsReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        draft = draft.filter((rep) => rep.id !== report.id);
        return draft;
      }),
    );
  };

  const setValue = context.setValue;
  const setError = context.setError;
  const trigger = context.trigger;

  const onReportInvalid = useCallback(
    (reportId: string) => {
      setInvalidCombinationReportIds((prev) => [...prev, reportId]);
      setError("custom-reports", {
        message: "Validation error",
      });
      trigger("custom-reports");
    },
    [setError, trigger],
  );

  const onReportValid = useCallback((reportId: string) => {
    setInvalidCombinationReportIds((prev) =>
      prev.filter((x) => x !== reportId),
    );
  }, []);

  useEffect(() => {
    if (reports.length === 0) {
      setValue("custom-reports", [], {
        shouldValidate: false,
      });
    }
  }, [reports.length, setValue]);

  useEffect(() => {
    //If there is any report where the combination is invalid, set report and don't evaluate further
    if (invalidCombinationReportIds.length > 0) {
      setError("custom-reports", {
        message: "Validation error",
      });
      return;
    }

    //Only evaluate when all reports are valid
    const getReports = () => {
      if (!reports.length) {
        return [];
      }
      const foundReports = reports.every((r) => {
        return !!r.name && !!r.fields.length && !!r.levels;
      })
        ? reports
        : undefined;
      return foundReports;
    };

    const currentReports = getReports();
    if (currentReports && Array.isArray(currentReports)) {
      //Check if all report names are unique
      const duplicateReportNames = currentReports.filter(
        (x) => currentReports.filter((y) => y.name === x.name).length > 1,
      );

      const _reportErrors: Record<string, string> = {};
      for (const duplicateReport of duplicateReportNames) {
        _reportErrors[duplicateReport.id] = "Report names must be unique";
      }

      setReportErrors(_reportErrors);
      if (duplicateReportNames.length > 0) {
        setError("custom-reports", {
          message: "Validation error",
        });
      } else {
        setValue("custom-reports", currentReports, {
          shouldValidate: true,
        });
      }
    }
  }, [reports, setValue, setError, invalidCombinationReportIds.length]);

  const handleAdAccountChange = (values: BasicFormOption[]) => {
    setValue("adaccount", values, {
      shouldValidate: true,
    });
    setAdAccount(values);
  };

  const handleClickWindowChange = (values: BasicFormOption[]) => {
    setValue("click_window", values, {
      shouldValidate: true,
    });
    setClickWindow(values);
  };

  const handleViewWindowChange = (values: BasicFormOption[]) => {
    setValue("view_window", values, {
      shouldValidate: true,
    });
    setViewWindow(values);
  };

  return (
    <div className="space-y-8">
      <div>
        <FieldLabel id="name" required={true}>
          Ad Accounts
        </FieldLabel>
        <AppliedMultiSelect
          isMulti={true}
          isLoading={false}
          placeholder="Select one or more"
          onChange={handleAdAccountChange}
          options={adAccountOptions}
          value={adaccount}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          Choose the ad accounts from which Weld should retrieve data.
        </InputDescription>
      </div>
      <div>
        <FieldLabel id="click_window" required={false}>
          Click Window
        </FieldLabel>
        <AppliedMultiSelect
          isMulti={true}
          isLoading={false}
          placeholder="Select one or more"
          onChange={handleClickWindowChange}
          options={facebookAdsClickWindowOptions}
          value={clickWindow}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          The selected click window will be applied to all default reports.
          Custom reports will not use this setting.
        </InputDescription>
      </div>
      <div>
        <FieldLabel id="view_window" required={false}>
          View Window
        </FieldLabel>
        <AppliedMultiSelect
          isMulti={true}
          isLoading={false}
          placeholder="Select one or more"
          onChange={handleViewWindowChange}
          options={facebookAdsViewWindowOptions}
          value={viewWindow}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          The selected view window will be applied to all default reports.
          Custom reports will not use this setting.
        </InputDescription>
      </div>
      {!!reports.length && (
        <div className="text-sm">Custom reports ({reports.length}/20)</div>
      )}
      {reports.map((r) => {
        return (
          <FacebookAdsCustomReport
            report={r}
            key={r.id}
            onChangeReport={changeReport}
            onRemoveReport={removeReport}
            initialBreakdownOptions={breakdownOptions}
            initialActionBreakdownOptions={actionBreakdownOptions}
            initialFieldOptions={fieldsOptions}
            initialLevelOptions={levelOptions}
            initialActionReportTimeOptions={actionReportTimeOptions}
            initialClickOptions={clickWindowOptions}
            initialViewOptions={viewWindowOptions}
            initialAggregationOptions={aggregationOptions}
            initialRollingWindowOptions={rollingWindowOptions}
            validationError={reportErrors[r.id]}
            connectionId={context.getValues()["connectionId"]}
            adAccounts={adaccount}
            onReportInvalid={onReportInvalid}
            onReportValid={onReportValid}
          />
        );
      })}
      <SecondaryButton
        onClick={() => {
          const reportId = uuid();
          setReports((prev) => [
            ...prev,
            {
              id: reportId,
              action_breakdowns: [],
              fields: [],
              breakdowns: [],
              action_report_time: {
                label: "impression",
                value: "impression",
              },
              levels: { label: "ad", value: "ad" },
              name: "",
              click_window: [{ label: "7d_click", value: "7d_click" }],
              view_window: [{ label: "1d_view", value: "1d_view" }],
              aggregation: FACEBOOK_ADS_DAILY_AGGREGATION,
              rolling_window: undefined,
            },
          ]);
          onReportInvalid(reportId);
        }}
      >
        Add custom report
      </SecondaryButton>
    </div>
  );
}
export const FacebookAdsReportNames = [
  "account",
  "action_canvas_component",
  "action_carousel_card",
  "action_conversion_device",
  "action_product_id",
  "action_video_sound",
  "action_video_view_type",
  "ad",
  "ad_insight",
  "ad_set",
  "ad_set_insight",
  "ad_set_roas_insight",
  "all_level",
  "campaign",
  "campaign_country_insight",
  "campaign_insight",
  "creative",
  "custom_audience",
  "custom_conversion",
  "demographics_age",
  "demographics_age_and_gender",
  "demographics_country",
  "demographics_country_ad",
  "demographics_delivery_device",
  "demographics_delivery_platform",
  "demographics_delivery_platform_ad",
  "demographics_delivery_platform_and_device",
  "demographics_dma_region",
  "demographics_gender",
  "demographics_region",
  "label",
] as const;
export type FacebookAdsReportType = (typeof FacebookAdsReportNames)[number];

export const isFacebookAdsReportType = (
  s: string,
): s is FacebookAdsReportType =>
  FacebookAdsReportNames.includes(s as FacebookAdsReportType);
